import { getMoveSectionTargetIndex } from "redux/actions/websiteBuilder/editor/actions/section/moveSection";
import { useCallback, useMemo } from "react";
import { IWebsiteBuilderClipboard } from "../../clipboard/useWebsiteBuilderClipboard";
import {
  TWebsiteBuilderSection,
  websiteBuilderGenerateSectionBounds,
} from "../../../nodes/section/websiteBuilderSection";
import { isWebsiteBuilderCopyableNode } from "../../../nodes/websiteBuilderNode";
import {
  websiteBuilderAddNode,
  websiteBuilderDeleteNode,
  websiteBuilderMoveSection,
  websiteBuilderSelectNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IWebsiteBuilderRoot } from "../../../nodes/root/websiteBuilderRoot";
import { cloneDeep } from "lodash";
import { useAppDispatch } from "hooks/redux";
import { TWebsiteBuilderActivePageDomNodeRefMap } from "../../activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { TWebsiteBuilderScrollContainerRef } from "../../../WebsiteBuilderEditor";
import { WEBSITE_BUILDER_HEADER_ID } from "../../../nodes/section/websiteBuilderHeader";
import { isWebsiteBuilderSectionEntity } from "../../../nodes/websiteBuilderSectionEntity";
import { TWebsiteBuilderDevice } from "../../../website/shared/device";
import { websiteBuilderSanitize } from "../../../sanitization/websiteBuilderSanitize";
import { IWebsiteBuilderFreeformParentContext, websiteBuilderPasteSectionEntityInFreeform } from "../../../nodes/websiteBuilderFreeformParentNode";

type TWebsiteBuilderMoveSectionUp = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;

type TWebsiteBuilderMoveSectionDown = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;

type TWebsiteBuilderCopySection = (
  node: TWebsiteBuilderSection,
) => void;

type TWebsiteBuilderPasteAtSection = (
  targetNode: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;

type TWebsiteBuilderDuplicateSection = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
) => void;

type TWebsiteBuilderDeleteSection = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

type TWebsiteBuilderCutSection = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  pageId: number,
  device: TWebsiteBuilderDevice,
) => void;

export type TWebsiteBuilderAddSectionDirection = "up" | "down";
type TWebsiteBuilderAddSection = (
  node: TWebsiteBuilderSection,
  root: IWebsiteBuilderRoot,
  targetNode: TWebsiteBuilderSection,
  direction: TWebsiteBuilderAddSectionDirection,
  pageId: number,
  device: TWebsiteBuilderDevice,
  activePageDomNodeRefMap: TWebsiteBuilderActivePageDomNodeRefMap,
  scrollContainerRef: TWebsiteBuilderScrollContainerRef,
) => void;

export interface IWebsiteBuilderSectionOperations {
  moveSectionUp: TWebsiteBuilderMoveSectionUp;
  moveSectionDown: TWebsiteBuilderMoveSectionDown;
  copySection: TWebsiteBuilderCopySection;
  pasteAtSection: TWebsiteBuilderPasteAtSection;
  duplicateSection: TWebsiteBuilderDuplicateSection;
  deleteSection: TWebsiteBuilderDeleteSection;
  cutSection: TWebsiteBuilderCutSection;
  addSection: TWebsiteBuilderAddSection;
}

export function useWebsiteBuilderSectionOperations(
  clipboard: IWebsiteBuilderClipboard,
): IWebsiteBuilderSectionOperations {
  const dispatch = useAppDispatch();

  const moveSectionUp: TWebsiteBuilderMoveSectionUp = useCallback(
    (node, root, pageId, device, activePageDomNodeRefMap, scrollContainerRef) => {
      const prevScrollTop = scrollContainerRef.current?.scrollTop;

      const prevIndex = root.children.findIndex(child => child.id === node.id);
      const nextIndex = getMoveSectionTargetIndex(prevIndex, "up", root.children, device);

      if (nextIndex === -1) {
        return;
      }

      dispatch(
        websiteBuilderMoveSection(pageId, device, node.id, {
          id: node.id,
          prevIndex,
          nextIndex,
        }),
      );

      requestAnimationFrame(() => {
        const sectionRef = activePageDomNodeRefMap.current.get(node.id);

        if (sectionRef && scrollContainerRef.current && prevScrollTop != null) {
          scrollContainerRef.current.scrollTo({ top: prevScrollTop });

          sectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    [],
  );

  const moveSectionDown: TWebsiteBuilderMoveSectionDown = useCallback(
    (node, root, pageId, device, activePageDomNodeRefMap, scrollContainerRef) => {
      const prevScrollTop = scrollContainerRef.current?.scrollTop;

      const prevIndex = root.children.findIndex(child => child.id === node.id);
      const nextIndex = getMoveSectionTargetIndex(prevIndex, "down", root.children, device);

      if (nextIndex === -1) {
        return;
      }

      dispatch(
        websiteBuilderMoveSection(pageId, device, node.id, {
          id: node.id,
          prevIndex,
          nextIndex,
        }),
      );

      requestAnimationFrame(() => {
        const sectionRef = activePageDomNodeRefMap.current.get(node.id);

        if (sectionRef && scrollContainerRef.current && prevScrollTop != null) {
          scrollContainerRef.current.scrollTo({ top: prevScrollTop });

          sectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    [],
  );

  const copySection: TWebsiteBuilderCopySection = useCallback(
    node => {
      if (isWebsiteBuilderCopyableNode(node)) {
        clipboard.copy(node);
      }
    },
    [clipboard],
  );

  const pasteAtSection: TWebsiteBuilderPasteAtSection = useCallback(
    (targetNode, root, pageId, device, activePageDomNodeRefMap, scrollContainerRef) => {
      const copiedNode = clipboard.peek();

      if (copiedNode?.type === "section") {
        const nodeToPaste: TWebsiteBuilderSection = cloneDeep(copiedNode);

        const targetNodeType = targetNode.section.type;

        let index: number = null;

        if (targetNodeType === "header") {
          index = 0;
        } else if (targetNodeType === "footer") {
          index = root.children.length;
        } else {
          const nodeIndex = root.children.findIndex(child => child.id === targetNode.id);

          if (nodeIndex === -1) {
            return;
          }

          index = nodeIndex + 1;
        }

        const prevScrollTop = scrollContainerRef.current?.scrollTop;

        const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToPaste.id, {
          snapshot: {
            inverseScrollNodeId: targetNode.id,
            parentId: root.id,
            node: nodeToPaste,
            index,
          },
        });

        if (addNodeAction == null) {
          return;
        }

        dispatch(addNodeAction);

        dispatch(
          websiteBuilderSelectNode({
            selectedNode: {
              type: "section",
              id: nodeToPaste.id,
              editingMode: {
                type: "none",
              },
            },
          }),
        );

        requestAnimationFrame(() => {
          const pastedNodeRef = activePageDomNodeRefMap.current.get(nodeToPaste.id);

          if (pastedNodeRef && scrollContainerRef.current && prevScrollTop != null) {
            const layoutShift = targetNodeType === "footer" ? pastedNodeRef.getBoundingClientRect().height : 0;

            scrollContainerRef.current.scrollTo({ top: prevScrollTop + layoutShift });

            setTimeout(() => {
              pastedNodeRef.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 200);
          }
        });
      } else if (isWebsiteBuilderSectionEntity(copiedNode)) {
        const desktopBounds = websiteBuilderGenerateSectionBounds(targetNode.section.layout, "desktop");
        const tabletBounds = websiteBuilderGenerateSectionBounds(targetNode.section.layout, "tablet");
        const mobileBounds = websiteBuilderGenerateSectionBounds(targetNode.section.layout, "mobile");

        const parentContext: IWebsiteBuilderFreeformParentContext = {
          node: targetNode,
          bounds: {
            desktop: desktopBounds,
            tablet: tabletBounds,
            mobile: mobileBounds,
          },
          ancestry: {
            sectionId: targetNode.id,
            containerId: null,
            gridCellId: null,
            tabId: null,
          },
        };

        websiteBuilderPasteSectionEntityInFreeform(
          copiedNode,
          parentContext,
          pageId,
          device,
          activePageDomNodeRefMap,
          scrollContainerRef,
          dispatch,
        );
      }
    },
    [clipboard],
  );

  const duplicateSection: TWebsiteBuilderDuplicateSection = useCallback((node, root, pageId, device, activePageDomNodeRefMap) => {
    const nodeType = node.section.type;
    const nodeIndex = root.children.findIndex(child => child.id === node.id);

    if (nodeType === "header" || nodeType === "footer" || nodeIndex === -1) {
      return;
    }

    const nodeToDuplicate: TWebsiteBuilderSection = websiteBuilderSanitize(node);

    const index = nodeIndex + 1;

    const addNodeAction = websiteBuilderAddNode(pageId, device, nodeToDuplicate.id, {
      snapshot: {
        inverseScrollNodeId: node.id,
        parentId: root.id,
        node: nodeToDuplicate,
        index,
      },
    });

    if (addNodeAction == null) {
      return;
    }

    dispatch(addNodeAction);

    dispatch(
      websiteBuilderSelectNode({
        selectedNode: {
          type: "section",
          id: nodeToDuplicate.id,
          editingMode: {
            type: "none",
          },
        },
      }),
    );

    requestAnimationFrame(() => {
      const duplicatedNodeRef = activePageDomNodeRefMap.current.get(nodeToDuplicate.id);

      if (duplicatedNodeRef) {
        setTimeout(() => {
          duplicatedNodeRef.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      }
    });
  }, []);

  const deleteSection: TWebsiteBuilderDeleteSection = useCallback((node, root, pageId, device) => {
    const index = root.children.findIndex(child => child.id === node.id);

    if (index === -1) {
      return;
    }

    let scrollNodeId: string = null;

    for (let previousIndex = index - 1; previousIndex >= 0; previousIndex--) {
      const previousSection = root.children[previousIndex];

      if (previousSection.section.visibility[device]) {
        scrollNodeId = previousSection.id;
        break;
      }
    }

    if (scrollNodeId == null) {
      scrollNodeId = root.headerVisibility[device] ? WEBSITE_BUILDER_HEADER_ID : root.id;
    }

    dispatch(
      websiteBuilderDeleteNode(pageId, device, scrollNodeId, {
        snapshot: {
          inverseScrollNodeId: node.id,
          parentId: root.id,
          node: cloneDeep(node),
          index,
        },
      }),
    );
  }, []);

  const cutSection: TWebsiteBuilderCutSection = useCallback(
    (node, root, pageId, device) => {
      copySection(node);
      deleteSection(node, root, pageId, device);
    },
    [copySection, deleteSection],
  );

  const addSection: TWebsiteBuilderAddSection = useCallback(
    (node, root, targetNode, direction, pageId, device, activePageDomNodeRefMap, scrollContainerRef) => {
      let index: number = null;

      const isHeader = targetNode.section.type === "header";
      const isFooter = targetNode.section.type === "footer";
      
      if (isHeader || isFooter) {
        if (isHeader && direction === "down") {
          index = 0;
        } else if (isFooter && direction === "up") {
          index = root.children.length;
        }
      } else {
        const targetNodeIndex = root.children.findIndex(child => child.id === targetNode.id);

        if (targetNodeIndex !== -1) {
          index = targetNodeIndex + (direction === "up" ? 0 : 1);
        }
      }
  
      if (index == null) {
        return;
      }
  
      const prevScrollTop = scrollContainerRef.current?.scrollTop;
    
      const addNodeAction = websiteBuilderAddNode(pageId, device, node.id, {
        snapshot: {
          inverseScrollNodeId: targetNode.id,
          parentId: root.id,
          node,
          index,
        },
      });

      if (addNodeAction == null) {
        return;
      }

      dispatch(addNodeAction);
  
      dispatch(
        websiteBuilderSelectNode({
          selectedNode: {
            type: "section",
            id: node.id,
            editingMode: {
              type: "none",
            },
          },
        }),
      );
  
      requestAnimationFrame(() => {
        const newSectionRef = activePageDomNodeRefMap.current.get(node.id);
  
        if (newSectionRef && scrollContainerRef.current && prevScrollTop != null) {
          const layoutShift = direction === "up" ? newSectionRef.getBoundingClientRect().height : 0;
  
          scrollContainerRef.current.scrollTo({ top: prevScrollTop + layoutShift });
  
          setTimeout(() => {
            newSectionRef.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 200);
        }
      });
    },
    [],
  );

  const sectionOperations: IWebsiteBuilderSectionOperations = useMemo(
    () => ({
      moveSectionUp,
      moveSectionDown,
      copySection,
      pasteAtSection,
      duplicateSection,
      deleteSection,
      cutSection,
      addSection,
    }),
    [moveSectionUp, moveSectionDown, copySection, pasteAtSection, duplicateSection, deleteSection, cutSection, addSection],
  );

  return sectionOperations;
}
